import { useChartContext } from '../components/chart.context';
import { getRandomHexColor, maColorMapping } from '../utils/chart.util';
import { LineSeries } from 'lightweight-charts';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  isMaIndicatorUpdate,
  MaIndicatorUpdate
} from '../services/indicator-update.service';
import { IndicatorType } from '@shared/types';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';

export const useMaSeries = () => {
  const {
    chartRef,
    overlayIndicatorSeriesRefs: indicatorSeriesRefs,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();

  const maIndicators = useMemo(
    () => indicatorConfigs.overlay.filter((config) => config.type === 'MA'),
    [indicatorConfigs.overlay]
  );
  const hasMa = useMemo(() => maIndicators.length > 0, [maIndicators]);
  // Keep latest indicator config by index so async stream updates can resolve series style.
  const maIndicatorsRef = useRef(maIndicators);

  useEffect(() => {
    maIndicatorsRef.current = maIndicators;
  }, [maIndicators]);

  const hasMaIndex = useCallback(
    (indicatorIndex: number) =>
      indicatorIndex >= 0 && indicatorIndex < maIndicatorsRef.current.length,
    []
  );

  // Lazily create the MA series when we have indicator config.
  const getOrCreateSeries = useCallback((index: number) => {
    if (!chartRef.current) return null;

    if (!indicatorSeriesRefs.current[IndicatorType.MA]) {
      indicatorSeriesRefs.current[IndicatorType.MA] = [];
    }

    const maSeries = indicatorSeriesRefs.current[IndicatorType.MA];

    if (!maSeries) return null;

    if (!maSeries[index]) {
      const period = maIndicatorsRef.current[index]?.period;
      maSeries[index] = chartRef.current.addSeries(LineSeries, {
        color: period
          ? maColorMapping[period] || getRandomHexColor()
          : getRandomHexColor(),
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });
    }

    return maSeries[index] ?? null;
  }, []);

  // Apply a full snapshot or incremental delta update.
  const applyUpdate = useCallback(
    (update: MaIndicatorUpdate) => {
      // Ignore stale indices from cached snapshots when MA config count changed.
      if (!hasMaIndex(update.indicatorIndex)) return;

      const maSeries = indicatorSeriesRefs.current[IndicatorType.MA];
      const existingSeries = maSeries?.[update.indicatorIndex] ?? null;
      const series = existingSeries || getOrCreateSeries(update.indicatorIndex);

      if (!series) return;

      if (update.updateType === 'snapshot') {
        // Keep full time grid with whitespace points to stay aligned with candle timeline.
        series.setData(
          update.data.map((item) =>
            typeof item.value === 'number'
              ? { time: item.time, value: item.value }
              : { time: item.time }
          )
        );
        return;
      }

      update.data.forEach((item) => {
        // Delta can carry missing value at a timestamp; emit whitespace instead of dropping the bar.
        series.update(
          typeof item.value === 'number'
            ? { time: item.time, value: item.value }
            : { time: item.time }
        );
      });
    },
    [getOrCreateSeries, hasMaIndex]
  );

  const clearMaSeries = useCallback(() => {
    const maSeries = indicatorSeriesRefs.current[IndicatorType.MA];
    if (!maSeries?.length) return;

    maSeries.forEach((series) => {
      if (!series) return;
      chartRef.current?.removeSeries(series);
    });

    indicatorSeriesRefs.current[IndicatorType.MA] = [];
  }, [chartRef, indicatorSeriesRefs]);

  useEffect(() => {
    if (hasMa) return;
    clearMaSeries();
  }, [clearMaSeries, hasMa]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasMa,
    indicatorType: IndicatorType.MA,
    isExpectedUpdate: isMaIndicatorUpdate,
    applyUpdate
  });
};
